"use client";
import React from "react";
import { usePropertyContext } from "components/pages/Account/Property/context";
import { SurveyBuilder } from "components/elements/SurveyBuilder";
import { assign } from "xstate";
import {
	DeepPartial,
	FileData,
	PropertyDetails,
	PropertyDetailsAppeal,
	PropertySurvey,
} from "utils/types";
import { useAuthContext } from "context/AuthContext";
import client from "utils/client";

import {
	getQuestionId,
	valueIsPropertySurveyQuestionState,
} from "components/pages/Account/Property/Survey/propertySurveyQuestionFlow.machine";
import { getMostRecentAppeal } from "utils/getMostRecentAppeal";
import merge from "lodash.merge";
import { getAcceptedFileTypes } from "elements/Input/FileUpload";
import { isValidDate } from "utils/validDate";
import moment from "moment";
import formatDollar, { formatDollarStr } from "utils/formatDollar";
import formatNumberWithCommas from "utils/formatNumberWithCommas";
import { EvidenceModal as PurchaseDateEvidenceModal } from "components/pages/Account/Property/Survey/questions/PurchaseDate";
import { EvidenceModal as IncomeProducingEvidenceModal } from "components/pages/Account/Property/Survey/questions/IncomeProducing";
import { EvidenceModal as NegativeImpactEvidenceModal } from "components/pages/Account/Property/Survey/questions/Easement";
import { EvidenceModal as FloodingEvidenceModal } from "components/pages/Account/Property/Survey/questions/Flooding";
import { EvidenceModal as FoundationIssuesEvidenceModal } from "components/pages/Account/Property/Survey/questions/FoundationIssues";
import { EvidenceModal as RoofDamageEvidenceModal } from "components/pages/Account/Property/Survey/questions/RoofDamage";
import { EvidenceModal as MajorDamageEvidenceModal } from "components/pages/Account/Property/Survey/questions/MajorDamage";
import { Services } from "utils/account/services";
import { shouldSeeProfile } from "utils/account";

import HandSigningDocumentWithBalanceSVG from "public/hand-signing-document-with-balance.svg";

export namespace EvidencePackage {
	type SurveyValues = {
		appeal?: Partial<
			Pick<
				PropertyDetailsAppeal,
				| "question_deferred"
				| "question_purchased"
				| "question_income"
				| "last_sale_date_prop"
				| "last_sale_price_prop"
				| "question_armslength"
				| "question_armslength_desc"
				| "files"
				| "q_structural_issues_list"
				| "q_flooded"
				| "q_negative_impact_list"
				| "q_negative_impact"
				| "q_negative_impact_cost"
				| "q_negative_impact_other"
				| "q_flooded_cause"
				| "q_flooded_desc"
				| "q_flooded_cost"
				| "q_flooded_date"
				| "q_structural_issues"
				| "q_structural_issues_cost"
				| "q_structural_issues_desc"
				| "q_catchall"
				| "q_catchall_desc"
				| "customer_completed_survey"
				| "customer_declined_survey"
			>
		>;
		property_survey?: Partial<
			Pick<
				PropertySurvey,
				| "q_major_damage_list"
				| "q_roof_damage"
				| "q_negative_impact_date"
				| "q_structural_issues_date"
				| "q_roof_damage_date"
				| "q_roof_damage_cost"
				| "q_roof_damage_desc"
				| "q_major_damage"
				| "q_major_damage_cost"
				| "q_major_damage_desc"
				| "q_major_damage_date"
				| "q_catchall_cost"
				| "q_catchall_date"
			>
		>;
	};

	interface OptionalInterfaces {}
	interface RequiredInterfaces {
		appeal?: Partial<Pick<PropertyDetailsAppeal, "appeal_id">>;
		property_details?: Partial<
			Pick<
				PropertyDetails,
				| "id"
				| "state"
				| "county"
				| "sqft_portal_resi_imprv"
				| "income_producing"
			>
		>;
		evidence_still_accepted?: boolean;
		survey_available?: boolean;
	}
	interface IContext
		extends SurveyBuilder.Flow.CompatibleInterface<
			SurveyValues,
			OptionalInterfaces,
			RequiredInterfaces
		> {}

	const initialContext: IContext = {
		tracking_values: {},
		survey_values: {},
		optional_interfaces: {},
		required_interfaces: {},
		survey_methods: {
			get: async () => {},
			update_backend: async () => {},
		},
		loading: true,
	};

	export const Context = React.createContext(initialContext);

	export const Provider: React.FC = ({ children }) => {
		const { details } = usePropertyContext();
		const { userData } = useAuthContext();
		const available_service_ids = Services.useAvailableServiceIds();

		const [loading, setLoading] = React.useState(true);
		const [survey_values, setSurveyValues] = React.useState<SurveyValues>({});

		const appeal = React.useMemo(() => getMostRecentAppeal(details), [details]);

		const survey_methods = React.useMemo(
			() => ({
				get: async () => {
					if (!appeal) return;
					try {
						setLoading(true);
						setSurveyValues({
							appeal: {
								question_deferred: appeal.question_deferred,
								question_income: appeal.question_income,
								question_purchased: appeal.question_purchased,
								files: appeal.files,
								last_sale_date_prop: appeal.last_sale_date_prop,
								last_sale_price_prop: appeal.last_sale_price_prop,
								question_armslength: appeal.question_armslength,
								question_armslength_desc: appeal.question_armslength_desc,
								q_structural_issues_list: appeal.q_structural_issues_list,
								q_flooded: appeal.q_flooded,
								q_negative_impact_list: appeal.q_negative_impact_list,
								q_negative_impact: appeal.q_negative_impact,
								q_negative_impact_cost: appeal.q_negative_impact_cost,
								q_negative_impact_other: appeal.q_negative_impact_other,
								q_flooded_cause: appeal.q_flooded_cause,
								q_flooded_desc: appeal.q_flooded_desc,
								q_flooded_cost: appeal.q_flooded_cost,
								q_flooded_date: appeal.q_flooded_date,
								q_structural_issues: appeal.q_structural_issues,
								q_structural_issues_cost: appeal.q_structural_issues_cost,
								q_structural_issues_desc: appeal.q_structural_issues_desc,
								q_catchall: appeal.q_catchall,
								q_catchall_desc: appeal.q_catchall_desc,
								customer_completed_survey: appeal.customer_completed_survey,
								customer_declined_survey: appeal.customer_declined_survey,
							},
							property_survey: {
								q_major_damage_list: appeal.q_major_damage_list,
								q_roof_damage: appeal.q_roof_damage,
								q_negative_impact_date: appeal.q_negative_impact_date,
								q_structural_issues_date: appeal.q_structural_issues_date,
								q_roof_damage_date: appeal.q_roof_damage_date,
								q_roof_damage_cost: appeal.q_roof_damage_cost,
								q_roof_damage_desc: appeal.q_roof_damage_desc,
								q_major_damage: appeal.q_major_damage,
								q_major_damage_cost: appeal.q_major_damage_cost,
								q_major_damage_desc: appeal.q_major_damage_desc,
								q_major_damage_date: appeal.q_major_damage_date,
								q_catchall_cost: appeal.q_catchall_cost,
								q_catchall_date: appeal.q_catchall_date,
							},
						});
					} catch (u: unknown) {
					} finally {
						setLoading(false);
					}
				},
				update_backend: async (inputs: {
					survey_values: DeepPartial<SurveyValues>;
					state_value?: string;
				}) => {
					if (!appeal) return;
					if (!valueIsPropertySurveyQuestionState(inputs.state_value)) {
						throw new Error(
							`Invalid property survey question state value ${inputs.state_value}`
						);
					}
					const activeQuestionId = getQuestionId(inputs.state_value);

					if (activeQuestionId === "evidenceIntro") {
						const question_deferred =
							inputs.survey_values.appeal?.question_deferred;
						const question_income =
							inputs.survey_values.appeal?.question_income;
						const question_purchased =
							inputs.survey_values.appeal?.question_purchased;
						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								deferredMaintenance: question_deferred ?? 0,
								recentPurchase: question_purchased ?? 0,
								incomeProducing: question_income ?? 0,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "purchasedRecently") {
						const purchase_price =
							inputs.survey_values.appeal?.last_sale_price_prop;
						const purchase_date =
							inputs.survey_values.appeal?.last_sale_date_prop;

						const payload = {
							value: true,
							price: purchase_price?.replace(/[^\d.]/g, "") || undefined,
							date: purchase_date
								? new Date(purchase_date).toISOString().slice(0, 10)
								: undefined,
						};

						await client.submitQuestionAnswer({
							appealId: details!.appeal_id,
							questionId: activeQuestionId,
							payload,
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "armsLengthTransaction") {
						const question_armslength =
							inputs.survey_values.appeal?.question_armslength;
						await client.submitQuestionAnswer({
							appealId: details!.appeal_id,
							questionId: activeQuestionId,
							payload: {
								value: question_armslength,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "armsLengthTransactionDescription") {
						const question_armslength_desc =
							inputs.survey_values.appeal?.question_armslength_desc;
						await client.submitQuestionAnswer({
							appealId: details!.appeal_id,
							questionId: activeQuestionId,
							payload: {
								desc: question_armslength_desc,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "incomeProducing") {
						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								value: !!inputs.survey_values.appeal?.question_income,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "damageAndDeferredMaintenanceIntro") {
						const q_flooded = inputs?.survey_values?.appeal?.q_flooded;
						const q_structural_issues_list =
							inputs?.survey_values?.appeal?.q_structural_issues_list;
						const q_roof_damage =
							inputs?.survey_values?.property_survey?.q_roof_damage;
						const q_major_damage_list =
							inputs?.survey_values?.property_survey?.q_major_damage_list;
						const q_negative_impact_list =
							inputs?.survey_values?.appeal?.q_negative_impact_list;

						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								q_flooded,
								q_structural_issues_list,
								q_roof_damage,
								q_major_damage_list,
								q_negative_impact_list,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "easement") {
						const q_negative_impact_list =
							inputs?.survey_values?.appeal?.q_negative_impact_list;
						const q_negative_impact_other =
							inputs?.survey_values?.appeal?.q_negative_impact_other;
						const q_negative_impact_cost =
							inputs?.survey_values?.appeal?.q_negative_impact_cost;
						const q_negative_impact_date =
							inputs?.survey_values?.property_survey?.q_negative_impact_date;

						await client.submitQuestionAnswer({
							appealId: details!.appeal_id,
							questionId: activeQuestionId,
							payload: {
								value: true,
								list: q_negative_impact_list,
								cost: q_negative_impact_cost,
								desc: q_negative_impact_other || undefined,
								date: q_negative_impact_date || undefined,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "flooding") {
						const q_flooded_cause =
							inputs?.survey_values?.appeal?.q_flooded_cause;
						const q_flooded_desc =
							inputs?.survey_values?.appeal?.q_flooded_desc;
						const q_flooded_cost =
							inputs?.survey_values?.appeal?.q_flooded_cost;
						const q_flooded_date =
							inputs?.survey_values?.appeal?.q_flooded_date;

						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								value: true,
								desc: q_flooded_desc || undefined,
								date: q_flooded_date || undefined,
								cost: q_flooded_cost || undefined,
								cause: q_flooded_cause || undefined,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "foundationIssues") {
						const q_structural_issues_list =
							inputs?.survey_values?.appeal?.q_structural_issues_list;
						const q_structural_issues_desc =
							inputs?.survey_values?.appeal?.q_structural_issues_desc;
						const q_structural_issues_cost =
							inputs?.survey_values?.appeal?.q_structural_issues_cost;
						const q_structural_issues_date =
							inputs?.survey_values?.property_survey?.q_structural_issues_date;

						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								value: true,
								desc: q_structural_issues_desc || undefined,
								date: q_structural_issues_date || undefined,
								cost: q_structural_issues_cost || undefined,
								list: q_structural_issues_list,
							},
						});
						return;
					}

					if (activeQuestionId === "roofDamage") {
						const q_roof_damage_date =
							inputs?.survey_values?.property_survey?.q_roof_damage_date;
						const q_roof_damage_desc =
							inputs?.survey_values?.property_survey?.q_roof_damage_desc;
						const q_roof_damage_cost =
							inputs?.survey_values?.property_survey?.q_roof_damage_cost;

						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								value: true,
								desc: q_roof_damage_desc || undefined,
								date: q_roof_damage_date || undefined,
								cost: q_roof_damage_cost || undefined,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "majorDamage") {
						const q_major_damage_list =
							inputs?.survey_values?.property_survey?.q_major_damage_list;
						const q_major_damage_desc =
							inputs?.survey_values?.property_survey?.q_major_damage_desc;
						const q_major_damage_cost =
							inputs?.survey_values?.property_survey?.q_major_damage_cost;
						const q_major_damage_date =
							inputs?.survey_values?.property_survey?.q_major_damage_date;
						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								value: true,
								desc: q_major_damage_desc || undefined,
								date: q_major_damage_date || undefined,
								cost: q_major_damage_cost || undefined,
								list: q_major_damage_list,
							},
						});
						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					if (activeQuestionId === "catchAll") {
						const q_catchall_date =
							inputs?.survey_values?.property_survey?.q_catchall_date;
						const q_catchall_desc =
							inputs?.survey_values?.appeal?.q_catchall_desc;
						const q_catchall_cost =
							inputs?.survey_values?.property_survey?.q_catchall_cost;

						await client.submitQuestionAnswer({
							questionId: activeQuestionId,
							appealId: details!.appeal_id,
							payload: {
								value: true,
								desc: q_catchall_desc || undefined,
								date: q_catchall_date || undefined,
								cost: q_catchall_cost || undefined,
							},
						});

						await client.submitQuestionAnswer({
							questionId: "confirmation",
							appealId: details!.appeal_id,
							payload: {},
						});

						setSurveyValues(prev => merge(prev, inputs.survey_values));
						return;
					}

					throw new Error(
						`survey_methods.patch: Missing patch implementation for ${activeQuestionId}`
					);
				},
			}),
			[appeal]
		);

		React.useEffect(() => {
			survey_methods.get();
		}, [survey_methods]);

		const evidence_still_available = React.useMemo(
			() => (details ? shouldSeeProfile(details) : false),
			[details]
		);

		const context = {
			loading,
			tracking_values: {
				property_id: details?.id,
				user_id: userData?.id,
			},
			survey_values,
			optional_interfaces: {},
			required_interfaces: {
				appeal: {
					appeal_id: appeal?.appeal_id,
				},
				property_details: {
					id: details?.id,
					county: details?.county,
					state: details?.state,
					sqft_portal_resi_imprv: details?.sqft_portal_resi_imprv,
					income_producing: details?.income_producing,
				},
				evidence_still_available,
				survey_available:
					available_service_ids?.includes("appeal") && evidence_still_available,
			},
			survey_methods,
		};

		return <Context.Provider value={context}>{children}</Context.Provider>;
	};

	export const useContext = () => {
		const context = React.useContext(Context);
		if (!context) {
			throw new Error(
				"EvidencePackage.useContext must be used within a EvidencePackage.Provider"
			);
		}
		return context;
	};

	export namespace Survey {
		const flowMachineConfig: SurveyBuilder.Flow.FlowMachineConfig<
			typeof Context
		> = {
			id: "EVIDENCE_PACKAGE",
			initial: "IDLE",
			context: initialContext,
			states: {
				IDLE: {
					on: {
						resume: {
							target: "ACTIVE_STEP",
						},
					},
				},
				RESTART: {
					always: [
						{
							target: "PURCHASED_RECENTLY",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_purchased === 1 &&
								context.survey_values?.appeal?.last_sale_price_prop === null &&
								context.survey_values?.appeal?.last_sale_date_prop === null,
						},
						{
							target: "ARMS_LENGTH_TRANSACTION",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_purchased === 1 &&
								context.survey_values?.appeal?.question_armslength === null,
						},
						{
							target: "ARMS_LENGTH_TRANSACTION_DESCRIPTION",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_purchased === 1 &&
								context.survey_values?.appeal?.question_armslength === 1 &&
								context.survey_values?.appeal?.question_armslength_desc ===
									null,
						},
						{
							target: "INCOME_PRODUCING",
							guard: ({ context }) =>
								(!!context.required_interfaces?.property_details
									?.income_producing ||
									context.survey_values?.appeal?.question_income === 1) &&
								!context.survey_values?.appeal?.files?.some(
									({ question }) => question === "incomeProducing"
								),
						},
						{
							target: "DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
						},
					],
				},
				ACTIVE_STEP: {
					always: [
						{
							target: "IDLE",
							guard: ({ context }) => context.loading,
						},
						{
							target: "COMPLETED",
							guard: ({ context }) =>
								!!context.survey_values?.appeal?.customer_completed_survey ||
								!!context.survey_values?.appeal?.customer_declined_survey,
						},
						{
							target: "NOT_AVAILABLE",
							guard: ({ context }) =>
								!context.required_interfaces?.survey_available,
						},
						{
							target: "PURCHASED_RECENTLY",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_purchased === 1 &&
								context.survey_values?.appeal?.last_sale_price_prop === null &&
								context.survey_values?.appeal?.last_sale_date_prop === null,
						},
						{
							target: "ARMS_LENGTH_TRANSACTION",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_purchased === 1 &&
								context.survey_values?.appeal?.question_armslength === null,
						},
						{
							target: "ARMS_LENGTH_TRANSACTION_DESCRIPTION",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_purchased === 1 &&
								context.survey_values?.appeal?.question_armslength === 1 &&
								context.survey_values?.appeal?.question_armslength_desc ===
									null,
						},
						{
							target: "INCOME_PRODUCING",
							guard: ({ context }) =>
								(!!context.required_interfaces?.property_details
									?.income_producing ||
									context.survey_values?.appeal?.question_income === 1) &&
								!context.survey_values?.appeal?.files?.some(
									({ question }) => question === "incomeProducing"
								),
						},
						{
							target: "DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_deferred !== 0 &&
								[
									context.survey_values?.appeal?.q_flooded,
									context.survey_values?.appeal?.q_structural_issues_list,
									context.survey_values?.property_survey?.q_roof_damage,
									context.survey_values?.property_survey?.q_major_damage_list,
									context.survey_values?.appeal?.q_negative_impact_list,
								].every(v => v === null),
						},
						{
							target: "EASEMENT",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_deferred === 1 &&
								context.survey_values?.appeal?.q_negative_impact_list !==
									null &&
								context.survey_values?.appeal?.q_negative_impact_cost === null,
						},
						{
							target: "FLOODING",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_deferred === 1 &&
								context.survey_values?.appeal?.q_flooded === 1 &&
								context.survey_values?.appeal?.q_flooded_cost === null,
						},
						{
							target: "FOUNDATION_ISSUES",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_deferred === 1 &&
								context.survey_values?.appeal?.q_structural_issues_list !==
									null &&
								context.survey_values?.appeal?.q_structural_issues_cost ===
									null,
						},
						{
							target: "ROOF_DAMAGE",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_deferred === 1 &&
								context.survey_values?.property_survey?.q_roof_damage === 1 &&
								context.survey_values?.property_survey?.q_roof_damage_cost ===
									null,
						},
						{
							target: "MAJOR_DAMAGE",
							guard: ({ context }) =>
								context.survey_values?.appeal?.question_deferred === 1 &&
								context.survey_values?.property_survey?.q_major_damage_list !==
									null &&
								context.survey_values?.property_survey?.q_major_damage_cost ===
									null,
						},
						{
							target: "CATCH_ALL",
						},
					],
				},
				PURCHASED_RECENTLY: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (1 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const last_sale_date_prop =
										mc?.survey_values?.appeal?.last_sale_date_prop;
									const last_sale_price_prop =
										mc?.survey_values?.appeal?.last_sale_price_prop;

									if (last_sale_date_prop && last_sale_price_prop) {
										return isValidDate(last_sale_date_prop);
									}

									return false;
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("purchasedRecently")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const last_sale_date_prop =
										machine_context?.survey_values?.appeal?.last_sale_date_prop;
									const last_sale_price_prop =
										machine_context?.survey_values?.appeal
											?.last_sale_price_prop;

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Property Purchase Information
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												A closing disclosure must include a property purchase
												price, date, signature, and realtor commissions.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text="The recent purchase of your property is one of the most important factors in our ability to lower your assessment."
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<PurchaseDateEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.TwoInputRow>
												<SurveyBuilder.Component.Fields.Date
													label="Purchase Date"
													required
													value={last_sale_date_prop ?? ""}
													setValue={v => {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	last_sale_date_prop: v,
																},
															},
														});
													}}
												/>
												<SurveyBuilder.Component.Fields.Dollar
													label="Total Purchase Price"
													required
													value={last_sale_price_prop ?? ""}
													setValue={v => {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	last_sale_price_prop: v,
																},
															},
														});
													}}
												/>
											</SurveyBuilder.Component.TwoInputRow>
											<SurveyBuilder.Component.Fields.Upload.Files
												label="Closing Disclosure"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes("purchasedRecently").join(
													","
												)}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: {
									target: "#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION",
									actions: ["track", "set_direction_forward"],
								},
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...context,
									state_value: "PURCHASED_RECENTLY",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("purchasedRecently", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"purchasedRecently-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "purchasedRecently",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: {
									target: "#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				ARMS_LENGTH_TRANSACTION: {
					entry: [
						"clear_error_message",
						assign(({ context, event }) => {
							return {
								progress: (2 / 12) * 100,
								is_step_valid: mc =>
									![null, undefined].includes(
										mc.survey_values?.appeal?.question_armslength as any
									),
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										update_machine_context,
										update_backend,
										machine_context,
										SkipButton,
									} = useFlow();

									const county =
										machine_context?.required_interfaces?.property_details
											?.county;
									const question_armslength =
										machine_context?.survey_values?.appeal?.question_armslength;
									return (
										<>
											<SurveyBuilder.Component.Title>
												Was this property purchased or transferred from a family
												member, friend, or company that you have a conflict
												interest with?
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Tooltip
												text={
													"Whether or not your property was purchased through an arm's length transaction changes how we approach building your case."
												}>
												Why provide this info?
											</SurveyBuilder.Component.Tooltip>
											<SurveyBuilder.Component.Fields.Radio.Templates.SolidBlocks
												layout="list"
												name="question_armslength"
												selection_state={question_armslength}
												set_selection_state={sv => {
													if (
														typeof sv !== "boolean" &&
														typeof sv !== "string"
													) {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	question_armslength: sv,
																},
															},
														});
														update_backend?.();
													}
												}}
												options={[
													{
														selection_value: 1 as 1,
														label: "Yes",
													},
													{
														selection_value: 0 as 0,
														label: "No",
													},
												]}
											/>
											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: {
									target: "#EVIDENCE_PACKAGE.PURCHASED_RECENTLY",
									actions: ["track", "set_direction_backward"],
								},
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								update_backend: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										guard: ({ context, event }) =>
											!!context.survey_values?.appeal?.question_armslength,
										target:
											"#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION_DESCRIPTION",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											!!context.required_interfaces?.property_details
												?.income_producing ||
											context.survey_values?.appeal?.question_income === 1,
										target: "#EVIDENCE_PACKAGE.INCOME_PRODUCING",
										actions: ["track", "set_direction_forward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						updating_answers: {
							entry: ["clear_error_message"],
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...context,
									state_value: "ARMS_LENGTH_TRANSACTION",
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context, event }) =>
											!!context.survey_values?.appeal?.question_armslength,
										target:
											"#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION_DESCRIPTION",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											!!context.required_interfaces?.property_details
												?.income_producing ||
											context.survey_values?.appeal?.question_income === 1,
										target: "#EVIDENCE_PACKAGE.INCOME_PRODUCING",
										actions: ["track"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				ARMS_LENGTH_TRANSACTION_DESCRIPTION: {
					entry: [
						"clear_error_message",
						assign(({ context, event }) => {
							return {
								progress: (3 / 12) * 100,
								is_step_valid: mc =>
									![null, undefined].includes(
										mc.survey_values?.appeal?.question_armslength_desc as any
									),

								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										update_machine_context,
										update_backend,
										machine_context,
										SkipButton,
									} = useFlow();

									const county =
										machine_context?.required_interfaces?.property_details
											?.county;
									const question_armslength_desc =
										machine_context?.survey_values?.appeal
											?.question_armslength_desc;
									return (
										<>
											<SurveyBuilder.Component.Title>
												Please describe the nature of the transaction.
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Tooltip
												text={
													"Whether or not your property was purchased through an arm's length transaction changes how we approach building your case."
												}>
												Why provide this info?
											</SurveyBuilder.Component.Tooltip>
											<SurveyBuilder.Component.Fields.TextArea
												label="Description of Transaction"
												value={question_armslength_desc ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																question_armslength_desc: e.target.value,
															},
														},
													});
												}}
											/>
											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: {
									target: "#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION",
									actions: ["track", "set_direction_backward"],
								},
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								update_backend: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										guard: ({ context }) =>
											!!context.required_interfaces?.property_details
												?.income_producing ||
											context.survey_values?.appeal?.question_income === 1,
										target: "#EVIDENCE_PACKAGE.INCOME_PRODUCING",
										actions: ["track", "set_direction_forward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						updating_answers: {
							entry: ["clear_error_message"],
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...context,
									state_value: "ARMS_LENGTH_TRANSACTION_DESCRIPTION",
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context }) =>
											!!context.required_interfaces?.property_details
												?.income_producing ||
											context.survey_values?.appeal?.question_income === 1,
										target: "#EVIDENCE_PACKAGE.INCOME_PRODUCING",
										actions: ["track"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				INCOME_PRODUCING: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (4 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const files_already_uploaded =
										mc?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("incomeProducing")
											)
											.map(({ file_name }) => file_name);

									return (
										!!files_already_uploaded?.length ||
										!!mc?.staged_file_uploads?.length
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("incomeProducing")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const previous_year = moment().year() - 1;

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);

									return (
										<>
											<SurveyBuilder.Component.Title>
												{!!machine_context?.survey_values?.appeal
													?.question_income ? (
													<>
														Please upload a monthly profit & loss statements and
														rent rolls by unit.
													</>
												) : (
													<>
														If this is an income producing property, please
														upload a monthly profit & loss statements and rent
														rolls by unit.
													</>
												)}
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												These documents are only required when a property has
												two or more units or is a commercial property.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text="Income producing property is assessed differently, typically requiring P&L and/or rent rolls."
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<IncomeProducingEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.Fields.Upload.Files
												label={`Last 3 years of Profit & Loss Statements & ${previous_year} Monthly Rent Rolls`}
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes("incomeProducing").join(
													","
												)}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_purchased === 1 &&
											context.survey_values?.appeal?.question_armslength === 1,
										target:
											"#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION_DESCRIPTION",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_purchased === 1,
										target: "#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION",
										actions: ["track", "set_direction_backward"],
									},
								],
								next: {
									target: "uploading_files",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("incomeProducing", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"incomeProducing-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "incomeProducing",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: {
									target: "updating_answers",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...context,
									survey_values: {
										...context.survey_values,
										appeal: {
											...context.survey_values.appeal,
											question_income: 1,
										},
									},
									state_value: "INCOME_PRODUCING",
								}),
							},
							on: {
								callback_success: [
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO: {
					entry: [
						"clear_error_message",
						assign(({ context, event }) => {
							return {
								progress: (5 / 12) * 100,
								is_step_valid: mc => {
									const q_major_damage_list =
										mc?.survey_values?.property_survey?.q_major_damage_list;
									const q_structural_issues_list =
										mc?.survey_values?.appeal?.q_structural_issues_list;
									const q_roof_damage =
										mc?.survey_values?.property_survey?.q_roof_damage;
									const q_flooded = mc?.survey_values?.appeal?.q_flooded;
									const q_negative_impact_list =
										mc?.survey_values?.appeal?.q_negative_impact_list;

									return !(
										q_major_damage_list === null &&
										q_structural_issues_list === null &&
										q_roof_damage === null &&
										q_flooded === null &&
										q_negative_impact_list === null
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										update_machine_context,
										update_backend,
										machine_context,
										SkipButton,
									} = useFlow();

									const {
										issue_checkbox_props,
										none_of_the_above_checkbox_props,
									} = React.useMemo(() => {
										const q_flooded =
											machine_context?.survey_values?.appeal?.q_flooded;
										const q_structural_issues_list =
											machine_context?.survey_values?.appeal
												?.q_structural_issues_list;
										const q_roof_damage =
											machine_context?.survey_values?.property_survey
												?.q_roof_damage;
										const q_major_damage_list =
											machine_context?.survey_values?.property_survey
												?.q_major_damage_list;
										const q_negative_impact_list =
											machine_context?.survey_values?.appeal
												?.q_negative_impact_list;

										const toggle_value_in_list = (
											list: string,
											value: string
										) => {
											const listArray = list
												.split(",")
												.map(v => v.trim())
												.filter(item => item !== "");
											let resultArray = listArray;
											if (listArray.includes(value)) {
												resultArray = listArray.filter(item => item !== value);
											} else {
												resultArray.push(value);
											}
											const result = resultArray.join(",");
											if (result === "") {
												return null;
											}
											return result;
										};

										const issue_checkbox_props: SurveyBuilder.Component.Fields.Checkbox.Props[] =
											[
												{
													label: "Flooding within past year",
													checked: q_flooded === 1,
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	q_flooded: e.target.checked ? 1 : 0,
																},
															},
														});
													},
												},
												{
													label: "Major gaps or cracks in foundation",
													checked: !!q_structural_issues_list?.includes(
														"Large cracks or gaps in foundation"
													),
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	q_structural_issues_list:
																		toggle_value_in_list(
																			q_structural_issues_list ?? "",
																			"Large cracks or gaps in foundation"
																		),
																},
															},
														});
													},
												},
												{
													label: "Damage to roof",
													checked: q_roof_damage === 1,
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																property_survey: {
																	q_roof_damage: q_roof_damage === 1 ? 0 : 1,
																},
															},
														});
													},
												},
												{
													label: "Wood rot or termite damage",
													checked: !!q_structural_issues_list?.includes(
														"Wood rot or termites in the structure"
													),
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	q_structural_issues_list:
																		toggle_value_in_list(
																			q_structural_issues_list ?? "",
																			"Wood rot or termites in the structure"
																		),
																},
															},
														});
													},
												},
												{
													label: "Damage from recent fire",
													checked: !!q_major_damage_list?.includes(
														"Damage from recent fire"
													),
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																property_survey: {
																	q_major_damage_list: toggle_value_in_list(
																		q_major_damage_list ?? "",
																		"Damage from recent fire"
																	),
																},
															},
														});
													},
												},
												{
													label: "Mold",
													checked: !!q_major_damage_list?.includes("Mold"),
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																property_survey: {
																	q_major_damage_list: toggle_value_in_list(
																		q_major_damage_list ?? "",
																		"Mold"
																	),
																},
															},
														});
													},
												},
												{
													label: "Easement on property",
													checked:
														!!q_negative_impact_list?.includes("Easement"),
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																appeal: {
																	q_negative_impact_list: toggle_value_in_list(
																		q_negative_impact_list ?? "",
																		"Easement"
																	),
																},
															},
														});
													},
												},
												{
													label: "Salvage or tear down property",
													checked: !!q_major_damage_list?.includes(
														"Salvage or tear down property"
													),
													onChange: e => {
														update_machine_context?.({
															survey_values: {
																property_survey: {
																	q_major_damage_list: toggle_value_in_list(
																		q_major_damage_list ?? "",
																		"Salvage or tear down property"
																	),
																},
															},
														});
													},
												},
											];
										const none_of_the_above_checkbox_props: SurveyBuilder.Component.Fields.Checkbox.Props[] =
											[
												{
													label: "None of the above",
													checked:
														q_major_damage_list === null &&
														q_structural_issues_list === null &&
														q_roof_damage === 0 &&
														q_flooded === 0 &&
														q_negative_impact_list === null,
													onChange: e => {
														if (e.target.checked) {
															update_machine_context?.({
																survey_values: {
																	appeal: {
																		q_flooded: 0,
																		q_negative_impact_list: null,
																		q_structural_issues_list: null,
																	},
																	property_survey: {
																		q_major_damage_list: null,
																		q_roof_damage: 0,
																	},
																},
															});
														} else {
															update_machine_context?.({
																survey_values: {
																	appeal: {
																		q_flooded: null,
																		q_negative_impact_list: null,
																		q_structural_issues_list: null,
																	},
																	property_survey: {
																		q_major_damage_list: null,
																		q_roof_damage: null,
																	},
																},
															});
														}
													},
												},
											];
										return {
											issue_checkbox_props,
											none_of_the_above_checkbox_props,
										};
									}, [machine_context]);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Do you have any damage or repairs that cost more than
												$5,000?
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Tooltip
												text={
													"Information related to damages over $5,000 along with documented evidence can help property owners earn a reduction on their appeal."
												}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											<SurveyBuilder.Component.Message.SunflowerLightest className="mt-2">
												<p className="sm semibold gold">
													Damage and deferred maintenance that would cost less
													than $5,000 to fix rarely assists in a reduction to
													the assessed value of a property.
												</p>
											</SurveyBuilder.Component.Message.SunflowerLightest>
											<SurveyBuilder.Component.Fields.Checkbox.Templates.SolidBlock
												className="mb-2"
												checkboxes={issue_checkbox_props}
											/>
											<SurveyBuilder.Component.Fields.Checkbox.Templates.SolidBlock
												checkboxes={none_of_the_above_checkbox_props}
											/>
											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											!!context.required_interfaces?.property_details
												?.income_producing ||
											context.survey_values?.appeal?.question_income === 1,
										target: "#EVIDENCE_PACKAGE.INCOME_PRODUCING",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_purchased === 1 &&
											context.survey_values?.appeal?.question_armslength === 1,
										target:
											"#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION_DESCRIPTION",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_purchased === 1,
										target: "#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION",
										actions: ["track", "set_direction_backward"],
									},
								],
								skip: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_negative_impact_list !==
											null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
							},
						},
						updating_answers: {
							entry: ["clear_error_message"],
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...context,
									state_value: "DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_negative_impact_list !==
											null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				EASEMENT: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (6 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const q_negative_impact_other =
										mc?.survey_values?.appeal?.q_negative_impact_other;
									const q_negative_impact_cost =
										mc?.survey_values?.appeal?.q_negative_impact_cost;
									const q_negative_impact_list =
										mc?.survey_values?.appeal?.q_negative_impact_list ?? "";
									const q_negative_impact_date =
										mc?.survey_values?.property_survey
											?.q_negative_impact_date ?? "";

									const dateIsValid =
										q_negative_impact_date.length === 0 ||
										q_negative_impact_date.length === 10;
									return !(
										!q_negative_impact_list.length ||
										!q_negative_impact_other ||
										!q_negative_impact_cost ||
										!dateIsValid
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("easement")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const q_negative_impact_list =
										machine_context?.survey_values?.appeal
											?.q_negative_impact_list;
									const q_negative_impact_other =
										machine_context?.survey_values?.appeal
											?.q_negative_impact_other;
									const q_negative_impact_cost =
										machine_context?.survey_values?.appeal
											?.q_negative_impact_cost;
									const q_negative_impact_date =
										machine_context?.survey_values?.property_survey
											?.q_negative_impact_date;

									const checkboxes = React.useMemo(() => {
										const reasons = [
											"Pipeline easement",
											"Utilities easement",
											"Drainage easement",
											"Other",
										];

										const props: SurveyBuilder.Component.Fields.Checkbox.Props[] =
											reasons.map(r => ({
												label: r,
												checked: q_negative_impact_list
													?.split(",")
													?.includes(r),
												onChange: () => {
													const list = q_negative_impact_list
														? q_negative_impact_list.split(",")
														: [];
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_negative_impact_list: (list.includes(r)
																	? list.filter(e => e !== r)
																	: list.concat(r)
																).join(","),
															},
														},
													});
												},
											}));
										return props;
									}, [machine_context]);

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const impact_under_5000 = React.useMemo(() => {
										return (
											q_negative_impact_cost !== null &&
											q_negative_impact_cost !== undefined &&
											q_negative_impact_cost < 5000
										);
									}, [q_negative_impact_cost]);

									const current_year = moment().year();

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Easement on the property
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												We need documentation like a deed showing an easement
												and photos of the negative impact to earn a tax
												reduction.{" "}
												<strong>
													The assessor’s office rarely grants a tax reduction
													without documentation of an easement or recently dated
													photos.
												</strong>
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text="We need documentation like a deed showing an easement and photos of the negative impact to earn a tax reduction. The assessor’s office rarely grants a tax reduction without documentation of an easement or recently dated photos."
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<NegativeImpactEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.Fields.Checkbox.Templates.SolidBlock
												className="mb-2"
												checkboxes={checkboxes}
											/>

											<SurveyBuilder.Component.Subtitle>
												Describe the easement's negative impact on the property*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Please describe how the easement effects the property
												including location effected.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.TextArea
												value={q_negative_impact_other ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_negative_impact_other: e.target.value,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Subtitle>
												What is the estimated impact to property value?*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Values under $5,000 are unlikely to have an impact on
												the assessment value.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Dollar
												containerClassName={impact_under_5000 ? "mb-0" : ""}
												value={
													q_negative_impact_cost
														? formatDollar(q_negative_impact_cost)
														: ""
												}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_negative_impact_cost: v
																	? Number(v.replace(/\D/g, "").trim())
																	: null,
															},
														},
													});
												}}
											/>
											{impact_under_5000 && (
												<p className="sm terracotta mb-2">
													Values less than {formatDollarStr(5000)}, are rarely
													helpful in getting an assessment reduction.
												</p>
											)}

											<SurveyBuilder.Component.Subtitle>
												Date of photos*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												The photos provided should depict the property as it
												appeared on January 1, {current_year}.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Date
												value={q_negative_impact_date ?? ""}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_negative_impact_date: v,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Fields.Upload.Files
												label="Deed granting easement and photos of impact"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes(
													"deferredMaintenance"
												).join(",")}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_backward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...merge(context, {
										survey_values: {
											appeal: { q_negative_impact: 1 },
										},
									}),
									state_value: "EASEMENT",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("easement", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"easement-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "easement",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				FLOODING: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (7 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const q_flooded_cause =
										mc?.survey_values?.appeal?.q_flooded_cause ?? "";
									const q_flooded_desc =
										mc?.survey_values?.appeal?.q_flooded_desc;
									const q_flooded_cost =
										mc?.survey_values?.appeal?.q_flooded_cost;
									const q_flooded_date =
										mc?.survey_values?.appeal?.q_flooded_date ?? "";

									const dateIsValid =
										q_flooded_date.length === 0 || q_flooded_date.length === 10;
									return !(
										!q_flooded_cause.length ||
										!q_flooded_desc ||
										!q_flooded_cost ||
										!dateIsValid
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("flooding")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const q_flooded_cause =
										machine_context?.survey_values?.appeal?.q_flooded_cause;
									const q_flooded_desc =
										machine_context?.survey_values?.appeal?.q_flooded_desc;
									const q_flooded_cost =
										machine_context?.survey_values?.appeal?.q_flooded_cost;
									const q_flooded_date =
										machine_context?.survey_values?.appeal?.q_flooded_date;

									const checkboxes = React.useMemo(() => {
										const reasons = [
											"Natural disaster",
											"Plumbing issues",
											"Property drainage issues",
										];

										const props: SurveyBuilder.Component.Fields.Checkbox.Props[] =
											reasons.map(r => ({
												label: r,
												checked: q_flooded_cause?.split(",")?.includes(r),
												onChange: () => {
													const list = q_flooded_cause
														? q_flooded_cause.split(",")
														: [];
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_flooded_cause: (list.includes(r)
																	? list.filter(e => e !== r)
																	: list.concat(r)
																).join(","),
															},
														},
													});
												},
											}));
										return props;
									}, [machine_context]);

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const impact_under_5000 = React.useMemo(() => {
										return (
											q_flooded_cost !== null &&
											q_flooded_cost !== undefined &&
											q_flooded_cost < 5000
										);
									}, [q_flooded_cost]);

									const current_year = moment().year();

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);
									return (
										<>
											<SurveyBuilder.Component.Title>
												Recent flooding
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												Quotes, invoices, and insurance estimates are the most
												effective ways to get a property tax reduction for
												flooding and water damage.{" "}
												<strong>
													The assessor’s office rarely grants a tax reduction
													without documentation showing the dollar value of
													repairs needed.
												</strong>
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text="If a property has ever flooded and sustained water damage, we can use evidence and anecdotes surrounding the flood to argue for a larger tax reduction on your behalf."
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<FloodingEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.Fields.Checkbox.Templates.SolidBlock
												checkboxes={checkboxes}
												className="mb-2"
											/>

											<SurveyBuilder.Component.Subtitle>
												Describe the water damage*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Describe how the flooding has impacted your home
												including location and any damage that remains because
												of the flood.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.TextArea
												value={q_flooded_desc ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_flooded_desc: e.target.value,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Subtitle>
												What is the estimated impact to property value?*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Upload repair estimates or insurance claims as support
												for this value. Values under $5,000 are unlikely to have
												an impact on the assessment value.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Dollar
												containerClassName={impact_under_5000 ? "mb-0" : ""}
												value={
													q_flooded_cost ? formatDollar(q_flooded_cost) : ""
												}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_flooded_cost: v
																	? Number(v.replace(/\D/g, "").trim())
																	: null,
															},
														},
													});
												}}
											/>
											{impact_under_5000 && (
												<p className="sm terracotta mb-2">
													Values less than {formatDollarStr(5000)}, are rarely
													helpful in getting an assessment reduction.
												</p>
											)}

											<SurveyBuilder.Component.Subtitle>
												Date of photos*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												The photos provided should depict the property as it
												appeared on January 1, {current_year}.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Date
												value={q_flooded_date ?? ""}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_flooded_date: v,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Fields.Upload.Files
												label="Quotes, Invoices and/or Photos showing proof of flood damage"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes(
													"deferredMaintenance"
												).join(",")}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_negative_impact_list !==
											null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track", "set_direction_backward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_backward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...merge(context, {
										survey_values: {
											appeal: { q_flooded: 1 },
										},
									}),
									state_value: "FLOODING",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("flooding", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"flooding-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "flooding",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				FOUNDATION_ISSUES: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (8 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const q_structural_issues_list =
										mc?.survey_values?.appeal?.q_structural_issues_list ?? "";
									const q_structural_issues_desc =
										mc?.survey_values?.appeal?.q_structural_issues_desc;
									const q_structural_issues_cost =
										mc?.survey_values?.appeal?.q_structural_issues_cost;
									const q_structural_issues_date =
										mc?.survey_values?.property_survey
											?.q_structural_issues_date ?? "";

									const dateIsValid =
										q_structural_issues_date.length === 0 ||
										q_structural_issues_date.length === 10;
									return !(
										!q_structural_issues_list.length ||
										!q_structural_issues_desc ||
										!q_structural_issues_cost ||
										!dateIsValid
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("foundationIssues")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const q_structural_issues_list =
										machine_context?.survey_values?.appeal
											?.q_structural_issues_list;
									const q_structural_issues_desc =
										machine_context?.survey_values?.appeal
											?.q_structural_issues_desc;
									const q_structural_issues_cost =
										machine_context?.survey_values?.appeal
											?.q_structural_issues_cost;
									const q_structural_issues_date =
										machine_context?.survey_values?.property_survey
											?.q_structural_issues_date;
									const checkboxes = React.useMemo(() => {
										const reasons = [
											"Large cracks or gaps in foundation",
											"Wood rot or termites in the structure",
											"Property drainage issues",
										];

										const props: SurveyBuilder.Component.Fields.Checkbox.Props[] =
											reasons.map(r => ({
												label: r,
												checked: q_structural_issues_list
													?.split(",")
													?.includes(r),
												onChange: () => {
													const list = q_structural_issues_list
														? q_structural_issues_list.split(",")
														: [];
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_structural_issues_list: (list.includes(r)
																	? list.filter(e => e !== r)
																	: list.concat(r)
																).join(","),
															},
														},
													});
												},
											}));
										return props;
									}, [machine_context]);

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const impact_under_5000 = React.useMemo(() => {
										return (
											q_structural_issues_cost !== null &&
											q_structural_issues_cost !== undefined &&
											q_structural_issues_cost < 5000
										);
									}, [q_structural_issues_cost]);

									const current_year = moment().year();

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Foundation and structural issues
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												Quotes, invoices, and insurance estimates are the most
												effective ways to get a property tax reduction for
												foundation and structural issues.{" "}
												<strong>
													The assessor’s office rarely grants a tax reduction
													without documentation showing the dollar value of
													repairs needed.
												</strong>
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text={
													"If a property has any foundation or structural issues, we can use evidence and anecdotes surrounding the issues to argue for a larger tax reduction on your behalf. If you're unsure if the problems are significant enough, always tell us about them, and we will do our best to use the information you give us to your benefit."
												}
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<FoundationIssuesEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.Fields.Checkbox.Templates.SolidBlock
												checkboxes={checkboxes}
												className="mb-2"
											/>

											<SurveyBuilder.Component.Subtitle>
												Description of issues*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Describe how the foundation or structural issues have
												impacted your home including location and any damage.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.TextArea
												value={q_structural_issues_desc ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_structural_issues_desc: e.target.value,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Subtitle>
												What is the estimated impact to property value?*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Upload repair estimates or insurance claims as support
												for this value. Values under $5,000 are unlikely to have
												an impact on the assessment value.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Dollar
												containerClassName={impact_under_5000 ? "mb-0" : ""}
												value={
													q_structural_issues_cost
														? formatDollar(q_structural_issues_cost)
														: ""
												}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_structural_issues_cost: v
																	? Number(v.replace(/\D/g, "").trim())
																	: null,
															},
														},
													});
												}}
											/>
											{impact_under_5000 && (
												<p className="sm terracotta mb-2">
													Values less than {formatDollarStr(5000)}, are rarely
													helpful in getting an assessment reduction.
												</p>
											)}

											<SurveyBuilder.Component.Subtitle>
												Date of photos*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												The photos provided should depict the property as it
												appeared on January 1, {current_year}.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Date
												value={q_structural_issues_date ?? ""}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_structural_issues_date: v,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Fields.Upload.Files
												label="Quotes, Invoices and/or Photos showing proof of damage"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes(
													"deferredMaintenance"
												).join(",")}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_negative_impact_list !==
											null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track", "set_direction_backward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_backward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...merge(context, {
										survey_values: {
											appeal: { q_structural_issues: 1 },
										},
									}),
									state_value: "FOUNDATION_ISSUES",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("foundationIssues", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"foundationIssues-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "foundationIssues",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				ROOF_DAMAGE: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (9 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const q_roof_damage_date =
										mc?.survey_values?.property_survey?.q_roof_damage_date ??
										"";
									const q_roof_damage_desc =
										mc?.survey_values?.property_survey?.q_roof_damage_desc;
									const q_roof_damage_cost =
										mc?.survey_values?.property_survey?.q_roof_damage_cost;

									const dateIsValid =
										q_roof_damage_date.length === 0 ||
										q_roof_damage_date.length === 10;
									return !(
										!q_roof_damage_desc ||
										!q_roof_damage_cost ||
										!dateIsValid
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("roofDamage")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const q_roof_damage_date =
										machine_context?.survey_values?.property_survey
											?.q_roof_damage_date;
									const q_roof_damage_desc =
										machine_context?.survey_values?.property_survey
											?.q_roof_damage_desc;
									const q_roof_damage_cost =
										machine_context?.survey_values?.property_survey
											?.q_roof_damage_cost;

									const current_year = moment().year();

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const impact_under_5000 = React.useMemo(() => {
										return (
											q_roof_damage_cost !== null &&
											q_roof_damage_cost !== undefined &&
											q_roof_damage_cost < 5000
										);
									}, [q_roof_damage_cost]);

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Roof damage
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												Quotes, invoices, and insurance estimates are the most
												effective ways to get a property tax reduction for
												needed roof replacements or repairs.{" "}
												<strong>
													The assessor’s office rarely grants a tax reduction
													without documentation showing the dollar value of
													repairs needed.
												</strong>
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text={
													"A structure's roof should be replaced every 25-30 years or if it has been damaged by a storm (wind, hail, ice, etc.) damage from a storm. Knowing when you last replaced your roof or if it has recently sustained damage will help us have all the information needed to argue for a larger tax reduction on your behalf."
												}
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<RoofDamageEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.Subtitle>
												Describe the roof damage or condition*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Describe the condition of the roof including location of
												damage and impact it has had on your property.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.TextArea
												value={q_roof_damage_desc ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_roof_damage_desc: e.target.value,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Subtitle>
												What is the estimated impact to property value?*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Upload repair estimates or insurance claims as support
												for this value. Values under $5,000 are unlikely to have
												an impact on the assessment value.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Dollar
												containerClassName={impact_under_5000 ? "mb-0" : ""}
												value={
													q_roof_damage_cost
														? formatDollar(q_roof_damage_cost)
														: ""
												}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_roof_damage_cost: v
																	? Number(v.replace(/\D/g, "").trim())
																	: null,
															},
														},
													});
												}}
											/>
											{impact_under_5000 && (
												<p className="sm terracotta mb-2">
													Values less than {formatDollarStr(5000)}, are rarely
													helpful in getting an assessment reduction.
												</p>
											)}

											<SurveyBuilder.Component.Subtitle>
												Date of photos*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												The photos provided should depict the property as it
												appeared on January 1, {current_year}.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Date
												value={q_roof_damage_date ?? ""}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_roof_damage_date: v,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Fields.Upload.Files
												label="Quotes, Invoices and/or Photos showing proof of damage"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes(
													"deferredMaintenance"
												).join(",")}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_negative_impact_list !==
											null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track", "set_direction_backward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_backward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: [
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_forward"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...merge(context, {
										survey_values: {
											appeal: { q_roof_damage: 1 },
										},
									}),
									state_value: "ROOF_DAMAGE",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("roofDamage", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"roofDamage-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "roofDamage",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: [
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track"],
									},
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				MAJOR_DAMAGE: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (10 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const q_major_damage_list =
										mc?.survey_values?.property_survey?.q_major_damage_list ??
										"";
									const q_major_damage_desc =
										mc?.survey_values?.property_survey?.q_major_damage_desc;
									const q_major_damage_cost =
										mc?.survey_values?.property_survey?.q_major_damage_cost;
									const q_major_damage_date =
										mc?.survey_values?.property_survey?.q_major_damage_date ??
										"";

									const dateIsValid =
										q_major_damage_date.length === 0 ||
										q_major_damage_date.length === 10;
									return !(
										!q_major_damage_list.length ||
										!q_major_damage_desc ||
										!q_major_damage_cost ||
										!dateIsValid
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("majorDamage")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);
									const q_major_damage_list =
										machine_context?.survey_values?.property_survey
											?.q_major_damage_list ?? "";
									const q_major_damage_desc =
										machine_context?.survey_values?.property_survey
											?.q_major_damage_desc;
									const q_major_damage_cost =
										machine_context?.survey_values?.property_survey
											?.q_major_damage_cost;
									const q_major_damage_date =
										machine_context?.survey_values?.property_survey
											?.q_major_damage_date ?? "";

									const checkboxes = React.useMemo(() => {
										const reasons = [
											"Mold",
											"Damage from recent fire",
											"Salvage or tear down property",
										];

										const props: SurveyBuilder.Component.Fields.Checkbox.Props[] =
											reasons.map(r => ({
												label: r,
												checked: q_major_damage_list?.split(",")?.includes(r),
												onChange: () => {
													const list = q_major_damage_list
														? q_major_damage_list.split(",")
														: [];
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_major_damage_list: (list.includes(r)
																	? list.filter(e => e !== r)
																	: list.concat(r)
																).join(","),
															},
														},
													});
												},
											}));
										return props;
									}, [machine_context]);

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const impact_under_5000 = React.useMemo(() => {
										return (
											q_major_damage_cost !== null &&
											q_major_damage_cost !== undefined &&
											q_major_damage_cost < 5000
										);
									}, [q_major_damage_cost]);

									const current_year = moment().year();

									const [evidence_modal_open, set_evidence_modal_open] =
										React.useState(false);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Major damage, deferred maintenance, or adverse
												conditions
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												Quotes, invoices, and insurance estimates are the most
												effective ways to get a property tax reduction for
												damage, deferred maintenance, and adverse conditions.{" "}
												<strong>
													The assessor’s office rarely grants a tax reduction
													without documentation showing the dollar value of
													repairs needed.
												</strong>
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Tooltip
												text={
													"If a property has any foundation or structural issues, we can use evidence and anecdotes surrounding the issues to argue for a larger tax reduction on your behalf. If you're unsure if the problems are significant enough, always tell us about them, and we will do our best to use the information you give us to your benefit."
												}
												onClick={() => set_evidence_modal_open(true)}>
												What evidence is helpful?
											</SurveyBuilder.Component.Tooltip>
											{evidence_modal_open && (
												<MajorDamageEvidenceModal
													setOpen={set_evidence_modal_open}
												/>
											)}
											<SurveyBuilder.Component.Fields.Checkbox.Templates.SolidBlock
												checkboxes={checkboxes}
												className="mb-2"
											/>

											<SurveyBuilder.Component.Subtitle>
												Description of issues*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Describe the issues including details on location and
												impact it has had on your property.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.TextArea
												value={q_major_damage_desc ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_major_damage_desc: e.target.value,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Subtitle>
												What is the estimated impact to property value?*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Upload repair estimates or insurance claims as support
												for this value. Values under $5,000 are unlikely to have
												an impact on the assessment value.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Dollar
												containerClassName={impact_under_5000 ? "mb-0" : ""}
												value={
													q_major_damage_cost
														? formatDollar(q_major_damage_cost)
														: ""
												}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_major_damage_cost: v
																	? Number(v.replace(/\D/g, "").trim())
																	: null,
															},
														},
													});
												}}
											/>
											{impact_under_5000 && (
												<p className="sm terracotta mb-2">
													Values less than {formatDollarStr(5000)}, are rarely
													helpful in getting an assessment reduction.
												</p>
											)}

											<SurveyBuilder.Component.Subtitle>
												Date of photos*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												The photos provided should depict the property as it
												appeared on January 1, {current_year}.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Date
												value={q_major_damage_date ?? ""}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_major_damage_date: v,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Fields.Upload.Files
												label="Quotes, Invoices and/or Photos showing proof of damage"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes(
													"deferredMaintenance"
												).join(",")}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											context.survey_values?.property_survey?.q_roof_damage ===
											1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.q_negative_impact_list !==
											null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track", "set_direction_backward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_backward"],
									},
								],
								skip: [
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...merge(context, {
										survey_values: {
											property_survey: { q_major_damage: 1 },
										},
									}),
									state_value: "MAJOR_DAMAGE",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("majorDamage", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"majorDamage-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "majorDamage",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: [
									{
										target: "#EVIDENCE_PACKAGE.CATCH_ALL",
										actions: ["track", "set_direction_forward"],
									},
								],
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				CATCH_ALL: {
					entry: [
						"clear_error_message",
						assign(({ context }) => {
							return {
								progress: (11 / 12) * 100,
								staged_file_uploads: [],
								is_step_valid: mc => {
									const q_catchall_date =
										mc?.survey_values?.property_survey?.q_catchall_date ?? "";
									const q_catchall_desc =
										mc?.survey_values?.appeal?.q_catchall_desc;
									const q_catchall_cost =
										mc?.survey_values?.property_survey?.q_catchall_cost;

									const dateIsValid =
										q_catchall_date.length === 0 ||
										q_catchall_date.length === 10;

									return !(
										!q_catchall_desc ||
										!q_catchall_cost ||
										!dateIsValid
									);
								},
								NavigationRow: ({ TemplateNavigationRow }) => (
									<TemplateNavigationRow />
								),
								Step: function () {
									const {
										machine_context,
										set_staged_file_uploads,
										update_machine_context,
										SkipButton,
									} = useFlow();

									const files_already_uploaded =
										machine_context?.survey_values?.appeal?.files
											?.filter(({ file_name }) =>
												file_name.startsWith("catchAll")
											)
											.map(({ file_name }) => file_name);

									const staged_file_uploads = React.useMemo(
										() => machine_context?.staged_file_uploads ?? [],
										[machine_context?.staged_file_uploads]
									);

									const q_catchall_date =
										machine_context?.survey_values?.property_survey
											?.q_catchall_date ?? "";
									const q_catchall_desc =
										machine_context?.survey_values?.appeal?.q_catchall_desc;
									const q_catchall_cost =
										machine_context?.survey_values?.property_survey
											?.q_catchall_cost;

									const current_year = moment().year();

									const sqft_portal_resi_imprv =
										machine_context?.required_interfaces?.property_details
											?.sqft_portal_resi_imprv;

									const show_documents_recommended_warning =
										React.useMemo(() => {
											if (!files_already_uploaded?.length) {
												return true;
											}
											return false;
										}, [files_already_uploaded]);

									const impact_under_5000 = React.useMemo(() => {
										return (
											q_catchall_cost !== null &&
											q_catchall_cost !== undefined &&
											q_catchall_cost < 5000
										);
									}, [q_catchall_cost]);

									return (
										<>
											<SurveyBuilder.Component.Title>
												Is there anything else about your property you’d like to
												share?
											</SurveyBuilder.Component.Title>
											<SurveyBuilder.Component.Body>
												Quotes, invoices, and insurance estimates are the most
												effective ways to get a property tax reduction for
												damage, deferred maintenance, and adverse conditions.{" "}
												<strong>
													The assessor’s office rarely grants a tax reduction
													without documentation showing the dollar value of
													repairs needed.
												</strong>
											</SurveyBuilder.Component.Body>
											<div className="mb-3" />
											<SurveyBuilder.Component.Message.SunflowerLightest>
												<ul className="white ml-2">
													{sqft_portal_resi_imprv && (
														<li className="sm denim-medium">
															If the square footage of your structure is less
															than{" "}
															<span className="no-translate">
																{formatNumberWithCommas(
																	Math.floor(sqft_portal_resi_imprv * 0.95)
																)}
															</span>{" "}
															sqft, please upload supporting documentation
															including builders sketch, appraisal, or property
															survey.
														</li>
													)}
													<li className="sm denim-medium">
														Damage and deferred maintenance that would cost less
														than $5,000 to fix rarely assist in a reduction to
														the assessed value of a property.
													</li>
													<li className="sm denim-medium">
														Documentation showing the dollar value of repairs is
														needed to support any description provided.
													</li>
												</ul>
											</SurveyBuilder.Component.Message.SunflowerLightest>

											<SurveyBuilder.Component.Subtitle>
												Describe the issues.*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Describe the issues including details on location and
												impact it has had on your property.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.TextArea
												value={q_catchall_desc ?? ""}
												onChange={e => {
													update_machine_context?.({
														survey_values: {
															appeal: {
																q_catchall_desc: e.target.value,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Subtitle>
												What is the estimated impact to property value?*
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												Upload repair estimates or insurance claims as support
												for this value. Values under $5,000 are unlikely to have
												an impact on the assessment value.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Dollar
												containerClassName={impact_under_5000 ? "mb-0" : ""}
												value={
													q_catchall_cost ? formatDollar(q_catchall_cost) : ""
												}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_catchall_cost: v
																	? Number(v.replace(/\D/g, "").trim())
																	: null,
															},
														},
													});
												}}
											/>
											{impact_under_5000 && (
												<p className="sm terracotta mb-2">
													Values less than {formatDollarStr(5000)}, are rarely
													helpful in getting an assessment reduction.
												</p>
											)}

											<SurveyBuilder.Component.Subtitle>
												Date of photos
											</SurveyBuilder.Component.Subtitle>
											<SurveyBuilder.Component.Body>
												The photos provided should depict the property as it
												appeared on January 1, {current_year}.
											</SurveyBuilder.Component.Body>
											<SurveyBuilder.Component.Fields.Date
												value={q_catchall_date ?? ""}
												setValue={v => {
													update_machine_context?.({
														survey_values: {
															property_survey: {
																q_catchall_date: v,
															},
														},
													});
												}}
											/>

											<SurveyBuilder.Component.Fields.Upload.Files
												label="Quotes, Invoices and/or Photos showing proof of damage"
												alreadyUploaded={files_already_uploaded}
												filesToUpload={staged_file_uploads}
												updateFilesToUpload={
													set_staged_file_uploads ?? (fn => {})
												}
												accept={getAcceptedFileTypes(
													"deferredMaintenance"
												).join(",")}
											/>

											{show_documents_recommended_warning && (
												<SurveyBuilder.Component.Message.Rust>
													<p>
														We recommend uploading supporting documentation
														before continuing. Evidence submitted without
														documentation rarely results in a reduction.
													</p>
												</SurveyBuilder.Component.Message.Rust>
											)}

											<SkipButton className="mt-3 mx-auto">Skip</SkipButton>
										</>
									);
								},
							};
						}),
					],
					initial: "idle",
					states: {
						idle: {
							on: {
								back: [
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_deferred === 1 &&
											context.survey_values?.property_survey
												?.q_major_damage_list !== null,
										target: "#EVIDENCE_PACKAGE.MAJOR_DAMAGE",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_deferred === 1 &&
											context.survey_values?.property_survey?.q_roof_damage ===
												1,
										target: "#EVIDENCE_PACKAGE.ROOF_DAMAGE",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_deferred === 1 &&
											context.survey_values?.appeal
												?.q_structural_issues_list !== null,
										target: "#EVIDENCE_PACKAGE.FOUNDATION_ISSUES",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_deferred === 1 &&
											context.survey_values?.appeal?.q_flooded === 1,
										target: "#EVIDENCE_PACKAGE.FLOODING",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_deferred === 1 &&
											context.survey_values?.appeal?.q_negative_impact_list !==
												null,
										target: "#EVIDENCE_PACKAGE.EASEMENT",
										actions: ["track", "set_direction_backward"],
									},
									{
										target:
											"#EVIDENCE_PACKAGE.DAMAGE_AND_DEFERRED_MAINTENANCE_INTRO",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											!!context.required_interfaces?.property_details
												?.income_producing ||
											context.survey_values?.appeal?.question_income === 1,
										target: "#EVIDENCE_PACKAGE.INCOME_PRODUCING",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_purchased === 1 &&
											context.survey_values?.appeal?.question_armslength === 1,
										target:
											"#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION_DESCRIPTION",
										actions: ["track", "set_direction_backward"],
									},
									{
										guard: ({ context }) =>
											context.survey_values?.appeal?.question_purchased === 1,
										target: "#EVIDENCE_PACKAGE.ARMS_LENGTH_TRANSACTION",
										actions: ["track", "set_direction_backward"],
									},
								],
								next: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
								skip: {
									target: "updating_answers",
									actions: ["track", "set_direction_forward"],
								},
							},
						},
						updating_answers: {
							invoke: {
								id: "update_backend",
								src: "update_backend",
								input: ({ context }) => ({
									...merge(context, {
										survey_values: {
											appeal: { q_catchall: 1 },
										},
									}),
									state_value: "CATCH_ALL",
								}),
							},
							on: {
								callback_success: {
									target: "uploading_files",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
						uploading_files: {
							invoke: {
								id: "upload_files",
								src: "upload_files",
								input: ({ context, event }) => ({
									context,
									handle_file_upload_request: async ({
										file_to_upload,
										on_progress,
									}) => {
										const appeal_id =
											context?.required_interfaces?.appeal?.appeal_id?.toString();
										if (!appeal_id) {
											throw new Error("No appeal id found");
										}
										const fd = new FormData();
										fd.append("catchAll", file_to_upload.file);
										await client.uploadFile({
											form: fd,
											appealId: appeal_id,
											onProgress: on_progress,
										});
									},
									set_file_uploads_results: ({ success }) => {
										return {
											staged_file_uploads: [],
											survey_values: {
												appeal: {
													files: [
														...success.map(file_to_upload => {
															const fileData: FileData = {
																file_name:
																	"catchAll-" +
																	file_to_upload.file.name.replace(/-/g, "_"),
																question: "catchAll",
																is_ownwell_document: 0,
															};
															return fileData;
														}),
													],
												},
											},
										};
									},
								}),
							},
							on: {
								callback_success: {
									target: "#EVIDENCE_PACKAGE.COMPLETED",
									actions: ["track"],
								},
								callback_error: {
									target: "idle",
									actions: ["track"],
								},
							},
						},
					},
				},
				COMPLETED: {
					type: "final",
					entry: [assign({ progress: 100 }), "exit"],
				},
				NOT_AVAILABLE: {
					type: "final",
					entry: [assign({ progress: 100 }), "exit"],
				},
			},
			on: {
				set_direction_forward: {
					actions: ["set_direction_forward"],
				},
				set_direction_backward: {
					actions: ["set_direction_backward"],
				},
				set_error_message: {
					actions: ["set_error_message"],
				},
				set_staged_file_uploads: {
					actions: ["set_staged_file_uploads"],
				},
				update_machine_context: {
					actions: ["update_machine_context", "track"],
				},
				put_machine_context: {
					actions: ["put_machine_context"],
				},
				init: {
					target: ".IDLE",
				},
				restart: {
					target: ".RESTART",
				},
				close: {
					actions: ["clear_error_message", "track", "exit"],
				},
				step_viewed: {
					actions: ["track"],
				},
			},
		};

		const flowMachine =
			SurveyBuilder.Flow.createFlowMachineFactory<
				typeof Context
			>().createMachine(flowMachineConfig);

		export const { FlowProvider, useFlow, FlowContext, FlowExit, useFlowExit } =
			SurveyBuilder.Flow.build({
				reactContext: Context,
				flowMachine,
			});

		export const Flow = () => {
			return (
				<SurveyBuilder.Templates.SingleFlow.FullScreen
					FlowContext={FlowContext}
				/>
			);
		};

		export const EmbeddedFlow = () => {
			return (
				<SurveyBuilder.Templates.SingleFlow.Embedded
					FlowContext={FlowContext}
				/>
			);
		};
	}
}

export const EvidencePackageSurvey: React.FC<{
	restart?: boolean;
}> = ({ restart }) => {
	const { details } = usePropertyContext();

	return (
		<SurveyBuilder.SurveySessionProvider>
			<EvidencePackage.Provider>
				<EvidencePackage.Survey.FlowProvider
					initial_event={restart ? { type: "restart" } : undefined}>
					<EvidencePackage.Survey.FlowExit
						exit={() => {
							if (details?.id) {
								window.location.href = `/account/properties/${details.id}`;
							}
						}}
					/>
					<EvidencePackage.Survey.Flow />
				</EvidencePackage.Survey.FlowProvider>
			</EvidencePackage.Provider>
		</SurveyBuilder.SurveySessionProvider>
	);
};
